<div class="flex flex-col gap-4 p-6 min-w-[720px] max-w-[900px]">

  <div class="flex items-center justify-between">
    <h3 class="text-lg font-bold">{{ titel() }}</h3>
    <span class="bm-badge bm-badge-neutral">Schritt {{ step() }} von 2</span>
  </div>

  @if (fehler()) {
    <div class="bm-alert bm-alert-error bm-alert-soft">
      <span class="text-sm">{{ fehler() }}</span>
    </div>
  }

  <!-- ================= Schritt 1: Auswahl ================= -->
  @if (step() === 1) {
    <div class="flex flex-wrap items-end gap-3">
      <div class="flex-1 min-w-[240px]">
        <label class="bm-label">Suche</label>
        <input
          type="text"
          class="bm-input w-full"
          [value]="searchTerm()"
          (input)="searchTerm.set($any($event.target).value)"
          (keyup.enter)="search()"
          [placeholder]="istTechnikerModus() ? 'Artikelvorlage search…' : 'Techniker search…'"
        />
      </div>

      @if (istTechnikerModus()) {
        <div class="min-w-[220px]">
          <label class="bm-label">Kunde</label>
          <!-- Auswahl über [selected] je Option, NICHT über [value] am Select:
               [value] ist eine rohe DOM-Property-Bindung und greift ins Leere,
               solange die @for-Optionen noch nicht im DOM stehen — der Browser
               zeigt dann die erste Option, obwohl ein anderer Wert gesetzt ist. -->
          <select class="bm-select w-full"
                  (change)="kundenFilter.set($any($event.target).value ? +$any($event.target).value : null); search()">
            <option value="" [selected]="kundenFilter() === null">Alle Kunden</option>
            @for (kunde of kunden(); track kunde.id) {
              <option [value]="kunde.id" [selected]="kundenFilter() === kunde.id">{{ kunde.name }}</option>
            }
          </select>
        </div>
      }

      <button type="button" class="bm-btn bm-btn-primary" (click)="search()" [disabled]="loading()">
        Suchen
      </button>
    </div>

    <!-- Projektfilter: vorbelegt aus der Artikelvorlage (Projekte aus comment2,
         sonst die Projekte der Kunden aus comment3), vom Lageristen änderbar. -->
    @if (!istTechnikerModus() && projekte().length > 0) {
      <div>
        <label class="bm-label">Projekttechniker aus</label>
        <div class="flex flex-wrap gap-1 max-h-32 overflow-y-auto p-2 border border-base-300 rounded-lg">
          @for (projekt of sortierteProjekte(); track projekt.id) {
            <!-- bm-btn-xs statt bm-badge: Badges sind im Styleguide reine
                 Anzeige-Elemente auf span-Ebene, für klickbare Umschalter
                 nutzt das Projekt die Button-Größen. -->
            <button type="button"
                    class="bm-btn bm-btn-xs"
                    [class.bm-btn-primary]="projektFilter().includes(projekt.id)"
                    [class.bm-btn-ghost]="!projektFilter().includes(projekt.id)"
                    (click)="projektUmschalten(projekt.id)">
              {{ projekt.name }}
            </button>
          }
        </div>
        <p class="text-xs text-base-content/70 mt-1">
          @if (projektFilter().length === 0) {
            Kein Filter — alle aktiven Techniker.
          } @else {
            {{ projektFilter().length }} von {{ projekte().length }} Projekten gewählt (stehen oben).
          }
        </p>
      </div>
    }

    <div class="flex items-center justify-between text-xs text-base-content/70">
      <span>{{ totalCount() }} Treffer · {{ selected().length }} ausgewählt</span>
      <button type="button" class="bm-btn bm-btn-ghost bm-btn-xs"
              (click)="alleGefilterten()"
              [disabled]="selectableMatches().length === 0">
        {{ allFilteredSelected() ? 'Auswahl dieser Seite aufheben' : 'Alle dieser Seite auswählen' }}
      </button>
    </div>

    <div class="border border-base-300 rounded-lg max-h-[380px] overflow-y-auto">
      @if (loading()) {
        <div class="p-6 text-center text-sm text-base-content/70">
          <span class="loading loading-spinner loading-sm mr-2"></span>Wird geladen…
        </div>
      } @else if (matches().length === 0) {
        <div class="p-6 text-center text-sm text-base-content/70">Keine Treffer.</div>
      } @else {
        @for (entry of matches(); track entry.id) {
          <!-- Bereits definierte Gegenstücke bleiben sichtbar, aber gesperrt:
               sonst sucht das Büro nach etwas, das scheinbar nicht existiert. -->
          <label class="flex items-center gap-3 px-3 py-2 border-b border-base-200 last:border-b-0"
                 [class.opacity-50]="entry.alreadyDefined"
                 [class.cursor-pointer]="!entry.alreadyDefined">
            <input type="checkbox"
                   class="bm-checkbox"
                   [disabled]="entry.alreadyDefined"
                   [checked]="selectedIds().has(entry.id)"
                   (change)="umschalten(entry)" />
            <span class="flex-1 text-sm">{{ entry.label }}</span>
            @if (entry.addressState) {
              <span class="bm-badge bm-badge-xs">{{ stateLabel(entry.addressState) }}</span>
            }
            @if (entry.alreadyDefined) {
              <span class="text-xs text-base-content/60">bereits definiert</span>
            }
          </label>
        }
      }
    </div>

    @if (seitenAnzahl() > 1) {
      <div class="flex items-center justify-center gap-2">
        <button type="button" class="bm-btn bm-btn-sm" [disabled]="seite() <= 1" (click)="search(seite() - 1)">
          Zurück
        </button>
        <span class="text-xs">Seite {{ seite() }} von {{ seitenAnzahl() }}</span>
        <button type="button" class="bm-btn bm-btn-sm" [disabled]="seite() >= seitenAnzahl()"
                (click)="search(seite() + 1)">
          Weiter
        </button>
      </div>
    }
  }

  <!-- ================= Schritt 2: Mengen ================= -->
  @if (step() === 2) {
    <div class="bm-alert bm-alert-info bm-alert-soft">
      <span class="text-xs">
        Aufgefüllt wird, sobald der Bestand <strong>unter</strong> den Mindestbestand fällt — bei
        Mindestbestand 1 also bei 0 Stück, nicht schon bei einem. Unterschreitet eine Vorlage, werden
        alle für den Techniker definierten Vorlagen bis zum Sollbestand aufgefüllt.
      </span>
    </div>

    <div class="flex items-end gap-3">
      <div class="min-w-[260px]">
        <label class="bm-label">Durchschnittswerte aus</label>
        <!-- Siehe Kommentar beim Kundenfilter: [selected] statt [value]. -->
        <select class="bm-select w-full"
                (change)="landGewechselt($any($event.target).value)">
          <option value="" [selected]="!durchschnittsLand()">Alle Techniker</option>
          @for (land of stateOptions; track land.code) {
            <option [value]="land.code" [selected]="durchschnittsLand() === land.code">{{ land.label }}</option>
          }
        </select>
      </div>
      <p class="text-xs text-base-content/70 pb-2">
        Der Wechsel setzt alle Abschnitte neu. Ohne Vergleichswerte bleibt 0 stehen und muss von Hand
        gefüllt werden.
      </p>
    </div>

    <div class="flex flex-col gap-4 max-h-[420px] overflow-y-auto">
      @for (group of groups(); track group.key) {
        <div class="border border-base-300 rounded-lg">
          @if (group.label) {
            <!-- Bundesland-Kopf: setzt beide Werte für alle Techniker der Gruppe
                 auf einmal, einzeln bleiben sie überschreibbar. -->
            <div class="flex flex-wrap items-end gap-3 px-3 py-2 bg-base-200/60 border-b border-base-300">
              <span class="font-semibold text-sm flex-1">{{ group.label }}</span>
              <div>
                <label class="bm-label text-xs">Minimum für alle</label>
                <input type="number" min="1" step="1" class="bm-input bm-input-sm w-24"
                       (input)="setzeGruppe(group.entries, 'min', $any($event.target).value)" />
              </div>
              <div>
                <label class="bm-label text-xs">Soll für alle</label>
                <input type="number" min="1" step="1" class="bm-input bm-input-sm w-24"
                       (input)="setzeGruppe(group.entries, 'target', $any($event.target).value)" />
              </div>
            </div>
          }

          @for (entry of group.entries; track entry.id) {
            <div class="flex flex-wrap items-end gap-3 px-3 py-2 border-b border-base-200 last:border-b-0">
              <span class="flex-1 text-sm min-w-[220px]">{{ entry.label }}</span>
              <div>
                <label class="bm-label text-xs">Mindestbestand</label>
                <input type="number" min="1" step="1" class="bm-input bm-input-sm w-24"
                       [class.border-error]="!rowValid(entry.id)"
                       [value]="quantities()[entry.id]?.min ?? 0"
                       (input)="setzeMenge(entry.id, 'min', $any($event.target).value)" />
              </div>
              <div>
                <label class="bm-label text-xs">Sollbestand</label>
                <input type="number" min="1" step="1" class="bm-input bm-input-sm w-24"
                       [class.border-error]="!rowValid(entry.id)"
                       [value]="quantities()[entry.id]?.target ?? 0"
                       (input)="setzeMenge(entry.id, 'target', $any($event.target).value)" />
              </div>
              @if (!rowValid(entry.id)) {
                <span class="text-xs text-error w-full">
                  Beide Werte müssen mindestens 1 betragen und der Sollbestand über dem Mindestbestand liegen.
                </span>
              }
            </div>
          }
        </div>
      }
    </div>
  }

  <!-- ================= Fussleiste ================= -->
  <div class="flex justify-between gap-3 pt-2 border-t border-base-300">
    <button type="button" class="bm-btn bm-btn-ghost" (click)="cancel()">Abbrechen</button>

    <div class="flex gap-3">
      @if (step() === 2) {
        <button type="button" class="bm-btn" (click)="zurueck()">Zurück</button>
        <button type="button" class="bm-btn bm-btn-primary" [disabled]="!allValid()" (click)="applyAll()">
          Alle übernehmen ({{ selected().length }})
        </button>
      } @else {
        <button type="button" class="bm-btn bm-btn-primary"
                [disabled]="selected().length === 0" (click)="goToStep2()">
          Weiter ({{ selected().length }})
        </button>
      }
    </div>
  </div>
</div>
